import * as Sentry from '@sentry/vue';
import { isDevOrDebug } from '@/config';

const logMethods = [
  'log',
  'warn',
  'error',
  'debug',
  'trace',
  'info',
  'table',
] as const;

type LogMethod = typeof logMethods[number];

function logToSentry (
  logMethod: LogMethod,
  ...messages: unknown[]
) {
  if (typeof messages[0] !== 'string') {
    return;
  }

  let attributes: Parameters<typeof Sentry.logger.trace>[1];
  if (messages.length > 1) {
    // Wrap the data in an object, because Sentry requires that.
    attributes = {
      rest: messages.slice(1),
    };
  }

  const data: Parameters<typeof Sentry.logger.trace> = [
    messages[0],
    attributes,
  ];

  switch (logMethod) {
    case 'error':
      Sentry.logger.error(...data);
      break;
    case 'warn':
      Sentry.logger.warn(...data);
      break;
    case 'info':
      Sentry.logger.info(...data);
      break;
    case 'debug':
      Sentry.logger.debug(...data);
      break;
    case 'trace':
    case 'log':
      Sentry.logger.trace(...data);
      break;
    case 'table':
      // Do nothing
      break;
    default:
      logMethod satisfies never;
      // Do nothing
      break;
  }
}

export function useDevLogger () {
  const loggers = {} as Record<LogMethod, (...messages: unknown[]) => void>;

  for (const method of logMethods) {
    loggers[method] = (...messages: unknown[]) => {
      if (isDevOrDebug) {
        console[method](...messages);
      }

      /*
        NOTE:
        In prod, we still log to Sentry if possible, but Sentry logging
        requires the first message to be a string and any next arguments
        to be plain objects. So not all logs may come through!
      */
      logToSentry(method, ...messages);
    };
  }

  return loggers;
}
